'use client';
import React, { useEffect, useRef } from 'react';
import {
  Zap, Calculator, Download, Printer, Layers, QrCode, Globe, Moon,
  Users, Clock, BarChart2, Smartphone, Cloud, Mail
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const features = [
  {
    id: 'feat-instant',
    icon: Zap,
    title: 'Instant Invoice Generator',
    desc: 'Create professional invoices in under 30 seconds with our smart auto-fill and template engine.',
    color: 'text-amber',
    bg: 'from-amber/15 to-amber/5',
  },
  {
    id: 'feat-gst',
    icon: Calculator,
    title: 'GST Calculator',
    desc: 'Automatic GST/CGST/SGST/IGST calculations with live updates as you type line items.',
    color: 'text-primary',
    bg: 'from-primary/15 to-primary/5',
  },
  {
    id: 'feat-pdf',
    icon: Download,
    title: 'PDF Download',
    desc: 'High-quality A4 PDF export with jsPDF. Multi-page support, crisp fonts, pixel-perfect layout.',
    color: 'text-red',
    bg: 'from-red/15 to-red/5',
  },
  {
    id: 'feat-print',
    icon: Printer,
    title: 'Print Invoice',
    desc: 'One-click browser print with optimized print stylesheet. Works on any printer worldwide.',
    color: 'text-sky',
    bg: 'from-sky/15 to-sky/5',
  },
  {
    id: 'feat-templates',
    icon: Layers,
    title: 'Invoice Templates',
    desc: '6 premium templates — Modern, Minimal, Corporate, Elegant, Dark, Creative. Switch instantly.',
    color: 'text-green',
    bg: 'from-green/15 to-green/5',
  },
  {
    id: 'feat-qr',
    icon: QrCode,
    title: 'QR Payment Support',
    desc: 'Auto-generate UPI QR codes for instant payment collection. Supports PhonePe, GPay, Paytm.',
    color: 'text-primary',
    bg: 'from-primary/15 to-primary/5',
  },
  {
    id: 'feat-currency',
    icon: Globe,
    title: 'Multi Currency',
    desc: 'Support for 30+ currencies including INR, USD, EUR, GBP, AED. Live rate display.',
    color: 'text-amber',
    bg: 'from-amber/15 to-amber/5',
  },
  {
    id: 'feat-dark',
    icon: Moon,
    title: 'Dark Mode',
    desc: 'Elegant dark theme with system detection and persistent preference. Easy on the eyes.',
    color: 'text-sky',
    bg: 'from-sky/15 to-sky/5',
  },
  {
    id: 'feat-clients',
    icon: Users,
    title: 'Client Management',
    desc: 'Save client profiles with billing/shipping addresses. Auto-fill on next invoice.',
    color: 'text-green',
    bg: 'from-green/15 to-green/5',
  },
  {
    id: 'feat-history',
    icon: Clock,
    title: 'Invoice History',
    desc: 'All invoices saved locally. Edit, duplicate, delete, or clone any past invoice in seconds.',
    color: 'text-red',
    bg: 'from-red/15 to-red/5',
  },
  {
    id: 'feat-analytics',
    icon: BarChart2,
    title: 'Analytics Dashboard',
    desc: 'Revenue charts, payment tracking, overdue alerts, and business insights at a glance.',
    color: 'text-primary',
    bg: 'from-primary/15 to-primary/5',
  },
  {
    id: 'feat-mobile',
    icon: Smartphone,
    title: 'Mobile Responsive',
    desc: 'Fully optimized for Android, iPhone, and tablets. Create invoices on the go.',
    color: 'text-amber',
    bg: 'from-amber/15 to-amber/5',
  },
  {
    id: 'feat-cloud',
    icon: Cloud,
    title: 'Cloud Ready',
    desc: 'Offline-first with LocalStorage. Optional cloud sync coming soon for cross-device access.',
    color: 'text-sky',
    bg: 'from-sky/15 to-sky/5',
  },
  {
    id: 'feat-email',
    icon: Mail,
    title: 'Email Sharing',
    desc: 'Share invoices directly via email with a professional HTML template or PDF attachment.',
    color: 'text-green',
    bg: 'from-green/15 to-green/5',
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.section-reveal') ?? [];
    els?.forEach((el) => obs?.observe(el));
    return () => obs?.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/components/FeaturesSection.tsx:139:4" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="139" data-component-end-line="174" data-component-file="FeaturesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22features%22%2C%22className%22%3A%22py-20%20bg-background%22%7D" id="features" ref={sectionRef} className="py-20 bg-background">
      <div data-component-id="src/app/components/FeaturesSection.tsx:140:6" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="140" data-component-end-line="173" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-screen-2xl%20mx-auto%20px-6%20lg%3Apx-10%22%7D" className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div data-component-id="src/app/components/FeaturesSection.tsx:141:8" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="141" data-component-end-line="153" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-14%20section-reveal%22%7D" className="text-center mb-14 section-reveal">
          <div data-component-id="src/app/components/FeaturesSection.tsx:142:10" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="142" data-component-end-line="145" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20px-4%20py-1.5%20rounded-full%20bg-primary%2F10%20border%20border-primary%2F20%20text-primary%20text-sm%20font-600%20mb-5%22%2C%22textContent%22%3A%22Everything%20You%20Need%22%7D" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-600 mb-5">
            <Zap data-component-id="src/app/components/FeaturesSection.tsx:143:12" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="143" data-component-end-line="143" data-component-file="FeaturesSection.tsx" data-component-name="Zap" data-component-content="%7B%22elementName%22%3A%22Zap%22%7D" size={13} />
            Everything You Need
          </div>
          <h2 data-component-id="src/app/components/FeaturesSection.tsx:146:10" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="146" data-component-end-line="149" data-component-file="FeaturesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-section-title%20font-extrabold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Powerful%20Features%2C%22%7D" className="text-section-title font-extrabold text-foreground mb-4">
            Powerful Features,{' '}
            <span data-component-id="src/app/components/FeaturesSection.tsx:148:12" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="148" data-component-end-line="148" data-component-file="FeaturesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22gradient-text%22%2C%22textContent%22%3A%22Zero%20Complexity%22%7D" className="gradient-text">Zero Complexity</span>
          </h2>
          <p data-component-id="src/app/components/FeaturesSection.tsx:150:10" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="150" data-component-end-line="152" data-component-file="FeaturesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20text-lg%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22From%20instant%20invoice%20creation%20to%20advanced%20analytics%20%E2%80%94%20every%20tool%20a%20freelancer%20or%20small%20business%20needs%2C%20built%20into%20one%20free%20platform.%22%7D" className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From instant invoice creation to advanced analytics — every tool a freelancer or small business needs, built into one free platform.
          </p>
        </div>

        <div data-component-id="src/app/components/FeaturesSection.tsx:155:8" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="155" data-component-end-line="172" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%202xl%3Agrid-cols-4%20gap-5%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
          {features?.map((feat, i) => {
            const Icon = feat?.icon;
            return (
              <div data-component-id="src/app/components/FeaturesSection.tsx:159:14" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="159" data-component-end-line="169" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22feature-card%20section-reveal%22%7D"
                key={feat?.id}
                className="feature-card section-reveal"
                style={{ transitionDelay: `${(i % 8) * 60}ms` }}
              >
                <div data-component-id="src/app/components/FeaturesSection.tsx:164:16" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="164" data-component-end-line="166" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feat?.bg} flex items-center justify-center mb-4`}>
                  <Icon data-component-id="src/app/components/FeaturesSection.tsx:165:18" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="165" data-component-end-line="165" data-component-file="FeaturesSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" size={22} className={feat?.color} />
                </div>
                <h3 data-component-id="src/app/components/FeaturesSection.tsx:167:16" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="167" data-component-end-line="167" data-component-file="FeaturesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-700%20text-foreground%20mb-2%20text-base%22%7D" className="font-700 text-foreground mb-2 text-base">{feat?.title}</h3>
                <p data-component-id="src/app/components/FeaturesSection.tsx:168:16" data-component-path="src/app/components/FeaturesSection.tsx" data-component-line="168" data-component-end-line="168" data-component-file="FeaturesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20leading-relaxed%22%7D" className="text-sm text-muted-foreground leading-relaxed">{feat?.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}